/* eslint-disable jsx-a11y/no-autofocus */
/* eslint-disable react/no-array-index-key */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { isEmpty } from 'lodash';

enum Keys {
  Backspace = 8,
  LeftArrow = 37,
  UpArrow = 38,
  RightArrow = 39,
  DownArrow = 40,
  E = 69,
}

interface Props
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value'> {
  fields?: number; // Количество полей
  onChangeCustom?: (value: string) => void; // принимает аргументом значение всех полей
  name: string;
  className?: string;
  error?: string;
}

function fillInitialArray(length: number) {
  return Array(length).fill('');
}

function keyCodeIsNumber(keyCode: number) {
  return (keyCode >= 48 && keyCode <= 57) || (keyCode >= 96 && keyCode <= 105);
}

export const SmsCodeInput: React.FC<Props> = ({
  className,
  fields = 4,
  type = 'number',
  error,
  onChangeCustom,
  ...rest
}) => {
  const [code, setCode] = useState<string[]>(fillInitialArray(fields));
  const [hasErrorStyle, setHasErrorStyle] = useState(false);

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const timerRef = useRef<number | null>(null);

  const resetErrorStyleMemoized = useCallback(() => {
    resetErrorStyle(fields);
  }, [fields]);

  // установка таймера на смену стилей инпутов при ошибке
  useEffect(() => {
    if (error) {
      setHasErrorStyle(true);
      timerRef.current = window.setTimeout(resetErrorStyleMemoized, 2000);
    }

    return () => clearTimeout(timerRef.current as number);
  }, [error, resetErrorStyleMemoized]);

  const handleChange = (e) => {
    let inputValue = String(e.target.value);

    if (type === 'number') {
      inputValue = inputValue.replace(/[^\d]/g, '');
    }

    const id = Number(e.target.dataset.id);

    if (inputValue !== '') {
      if (inputValue.length > 1) {
        inputValue.split('').forEach((chart, i) => {
          if (id + i < fields) {
            code[id + i] = chart;
          }
        });
      } else {
        code[id] = inputValue;
      }

      const actualCodeLength = code.join('').length;
      const newTarget =
        inputRefs.current[
          actualCodeLength < fields ? actualCodeLength : actualCodeLength - 1
        ];

      if (newTarget) {
        newTarget.focus();
      }

      setCode([...code]);
    }

    const fullValue = code.join('');

    if (fullValue) {
      onChangeCustom?.(fullValue);
    }
  };

  const handleKeyDown = (e) => {
    const id = Number(e.target.dataset.id);
    const nextTarget = inputRefs.current[id + 1];
    const prevTarget = inputRefs.current[id - 1];

    let inputRef;

    switch (e.keyCode) {
      case Keys.Backspace:
        e.preventDefault();

        inputRef = inputRefs.current[id];
        if (inputRef) {
          inputRef.value = '';
        }

        code[id] = '';
        setCode([...code]);

        if (inputRef?.value === '') {
          if (prevTarget) {
            prevTarget.focus();
          }
        }

        onChangeCustom?.(code.join(''));

        break;

      case Keys.LeftArrow:
        e.preventDefault();
        if (prevTarget) {
          prevTarget.focus();
        }
        break;

      case Keys.RightArrow:
        e.preventDefault();
        if (nextTarget) {
          nextTarget.focus();
        }
        break;

      case Keys.UpArrow:
      case Keys.DownArrow:
        e.preventDefault();
        break;

      case Keys.E:
        if (e.target.type === 'number') {
          e.preventDefault();
          break;
        }
        break;

      default:
        if (e.target.type === 'number') {
          if (!keyCodeIsNumber(e.keyCode)) {
            e.preventDefault();
          }
          break;
        }
        break;
    }
  };

  const handleFieldClick = () => {
    if (hasErrorStyle) {
      resetErrorStyleMemoized();
    }
  };

  function resetErrorStyle(countFields: number) {
    setHasErrorStyle(false);
    setCode(fillInitialArray(countFields));

    inputRefs?.current?.[0]?.focus();

    clearTimeout(timerRef.current as number);
    timerRef.current = null;
  }

  const errorStyle: React.CSSProperties = {
    background: '#f9eaec',
    border: '1px solid #d0021b',
  };

  return (
    <>
      <Container className={className}>
        {code.map((inputValue, i) => (
          <StyledInput
            ref={(ref) => {
              inputRefs.current[i] = ref;
            }}
            data-id={i}
            autoFocus={i === 0}
            value={inputValue}
            key={i}
            type={type}
            min={0}
            max={9}
            autoComplete="off"
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onClick={handleFieldClick}
            style={hasErrorStyle ? errorStyle : {}}
            {...rest}
          />
        ))}
      </Container>
      {!isEmpty(error) && <ErrorMessage>{error}</ErrorMessage>}
    </>
  );
};

const Container = styled.div`
  display: flex;
  justify-content: center;
`;

const StyledInput = styled.input`
  padding: 15px 8px 17px;
  width: 40px;
  height: 48px;
  border: 1px solid #c2c5cc;
  border-radius: 4px;
  background: #fff;
  font-weight: bold;
  font-size: 14px;
  line-height: 16px;
  color: #17181a;
  text-align: center;

  :not(:first-child) {
    margin-left: 16px;
  }

  :disabled {
    background: #f5f3f2;
    border: 1px solid #c2c5cc;
  }

  :focus {
    outline: none;
    border: 1px solid #007aff;
  }

  -moz-appearance: textfield;

  ::-webkit-outer-spin-button,
  ::-webkit-inner-spin-button {
    -webkit-appearance: none;
  }

  @media screen and (max-width: 480px) {
    font-size: 16px;
  }
`;

const ErrorMessage = styled.div`
  margin-top: 4px;
  font-size: 14px;
  line-height: 16px;
  text-align: center;
  color: #d0021b;
`;
